/**
 * Chart Detection Utility
 * Analyzes query results and SQL to determine if data should be visualized
 */

/**
 * Detects if query results should be visualized as a chart
 * @param {Object} result - Query result object with data, sql, etc.
 * @returns {Object|null} - Chart configuration or null if not visualizable
 */
export const detectChartType = (result) => {
  if (!result || !result.data || result.data.length === 0) {
    return null;
  }

  const { data, sql } = result;
  const columns = Object.keys(data[0]);
  const sqlLower = sql.toLowerCase();

  // Check for COUNT + GROUP BY pattern
  const hasCount = sqlLower.includes('count(');
  const hasGroupBy = sqlLower.includes('group by');

  if (hasCount && hasGroupBy && columns.length >= 2) {
    // Detect which column is the count
    const countColumn = columns.find(col =>
      ['count', 'total', 'num', 'number', 'cnt'].some(keyword =>
        col.toLowerCase().includes(keyword)
      )
    );

    if (countColumn) {
      // Get all non-count columns for category label
      const categoryColumns = columns.filter(col => col !== countColumn);

      if (categoryColumns.length > 0) {
        // Use first column as primary category, or combine multiple columns
        const categoryColumn = categoryColumns[0];

        // Determine chart type based on number of categories
        const chartType = data.length <= 10 ? 'pie' : 'bar';

        return {
          type: chartType,
          categoryColumn,
          valueColumn: countColumn,
          data: data.map(row => {
            // If multiple category columns, combine them for the label
            let label;
            if (categoryColumns.length === 1) {
              label = row[categoryColumn] || 'Unknown';
            } else {
              // Combine multiple columns (e.g., "Dave Wang - Unknown Institution")
              label = categoryColumns
                .map(col => row[col])
                .filter(val => val && val !== 'Unknown' && val !== 'Unknown Institution')
                .join(' - ') || 'Unknown';
            }

            return {
              name: label,
              value: parseInt(row[countColumn]) || 0
            };
          })
        };
      }
    }
  }

  // Check for time series (dates + counts)
  const hasDate = columns.some(col =>
    ['date', 'year', 'month', 'day', 'time'].some(keyword =>
      col.toLowerCase().includes(keyword)
    )
  );

  if (hasDate && columns.length === 2) {
    const dateColumn = columns.find(col =>
      ['date', 'year', 'month', 'day', 'time'].some(keyword =>
        col.toLowerCase().includes(keyword)
      )
    );

    const valueColumn = columns.find(col => col !== dateColumn);

    if (dateColumn && valueColumn) {
      return {
        type: 'line',
        categoryColumn: dateColumn,
        valueColumn,
        data: data.map(row => ({
          name: row[dateColumn] || 'Unknown',
          value: parseInt(row[valueColumn]) || 0
        }))
      };
    }
  }

  return null;
};

/**
 * Get a human-readable description of the chart
 * @param {Object} chartConfig - Chart configuration from detectChartType
 * @returns {string} - Description text
 */
export const getChartDescription = (chartConfig) => {
  if (!chartConfig) return '';

  const { type, categoryColumn, valueColumn } = chartConfig;

  switch (type) {
    case 'bar':
      return `Showing ${valueColumn} by ${categoryColumn}`;
    case 'pie':
      return `Distribution of ${valueColumn} by ${categoryColumn}`;
    case 'line':
      return `Trend of ${valueColumn} over ${categoryColumn}`;
    default:
      return 'Visualization';
  }
};
